/**
 * Deterministic mapping from a label name to a Spark TagColoredVariant.
 * Uses the same sorted-index approach as `getPredictionColor` in utils.jsx so
 * the same label always maps to the same color token.
 *
 * Order mirrors the visual weight progression in the Figma DS palette
 * (TagColored variants: pink → burgundy → purple → blue → cyan → lime → volcano → peach → yellow).
 */

/**
 * Concrete colors of each Spark `TagColored` variant (mirrors the
 * `--primitive-alternative-*` tokens in spark/src/styles/tokens.css). Use these
 * when a Spark tag look is needed on a non-Spark element (e.g. antd Tag/Select).
 */
export const SPARK_TAG_VARIANT_COLORS = {
  pink:     { bg: '#FFF0F6', color: '#C81376' },
  burgundy: { bg: '#FDECFC', color: '#A6119A' },
  purple:   { bg: '#F9F0FF', color: '#6228B4' },
  blue:     { bg: '#F6F9FE', color: '#163EE4' },
  cyan:     { bg: '#E6FFFB', color: '#0C7D7D' },
  lime:     { bg: '#FCFFE6', color: '#5B7B0A' },
  volcano:  { bg: '#FFF2E8', color: '#D23805' },
  peach:    { bg: '#FFEED2', color: '#A5441F' },
  yellow:   { bg: '#FFF9C2', color: '#896015' },
  disabled: { bg: '#f5f5f5', color: '#8c8c8c' },
};

/** @type {import('@spark').TagColoredVariant[]} */
const SPARK_TAG_VARIANTS = [
  'blue',
  'lime',
  'pink',
  'purple',
  'cyan',
  'burgundy',
  'volcano',
  'peach',
  'yellow',
];

/**
 * Returns the Spark `TagColoredVariant` for `labelName` within a sorted set of
 * all known labels.  When `allLabels` is supplied, the label's sorted position
 * is used to guarantee a stable, predictable color per dataset; otherwise a
 * simple string-hash fallback is used so standalone callers still work.
 *
 * @param {string} labelName
 * @param {string[]} allLabels
 * @returns {import('@spark').TagColoredVariant}
 */
export function getSparkTagVariant(labelName, allLabels = []) {
  // Callers can pass undefined/null (e.g. antd's tagRender is invoked with an
  // undefined value for the overflow "+N" rest item); render those as disabled.
  if (typeof labelName !== 'string' || labelName.length === 0) {
    return 'disabled';
  }

  const sorted = Array.from(new Set(allLabels)).sort((a, b) => a.localeCompare(b));
  const idx = sorted.indexOf(labelName);

  if (idx !== -1) {
    return SPARK_TAG_VARIANTS[idx % SPARK_TAG_VARIANTS.length];
  }

  // Fallback: hash the label name so unknown labels still get a stable color.
  let hash = 0;
  for (let i = 0; i < labelName.length; i++) {
    hash = (hash * 31 + labelName.charCodeAt(i)) >>> 0;
  }
  return SPARK_TAG_VARIANTS[hash % SPARK_TAG_VARIANTS.length];
}
